import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { ArrowDownCircle, ArrowUpCircle } from 'lucide-react';
import { apiClient } from '../../utils/apiHelper';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InventoryLedger = () => {
  const [ledger, setLedger] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchLedger();
  }, []);

  const fetchLedger = async () => {
    try {
      let url = '/api/inventory/reports/ledger';
      if (startDate && endDate) {
        url += `?start_date=${startDate}&end_date=${endDate}`;
      }
      const response = await apiClient.get(url);
      setLedger(response.data || { transactions: [], summary: {} });
    } catch (error) {
      console.error('Error:', error);
      setLedger({ transactions: [], summary: {} });
    }
  };

  const handleFilter = () => {
    fetchLedger();
  };

  if (!ledger) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Inventory Ledger</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Start Date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div>
              <Label>End Date</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
            <div className="flex items-end">
              <Button onClick={handleFilter} className="w-full">Filter</Button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <ArrowDownCircle className="w-8 h-8 text-green-600" />
                <div>
                  <div className="text-sm text-gray-600">Total Receives</div>
                  <div className="text-2xl font-bold">{ledger.total_receives}</div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <ArrowUpCircle className="w-8 h-8 text-red-600" />
                <div>
                  <div className="text-sm text-gray-600">Total Issues</div>
                  <div className="text-2xl font-bold">{ledger.total_issues}</div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">Type</th>
                  <th className="text-left p-3">Reference #</th>
                  <th className="text-left p-3">Details</th>
                  <th className="text-left p-3">Amount</th>
                </tr>
              </thead>
              <tbody>
                {ledger.transactions.length === 0 ? (
                  <tr><td colSpan="5" className="text-center p-4 text-gray-500">No transactions found.</td></tr>
                ) : (
                  ledger.transactions.map((transaction, idx) => (
                    <tr key={idx} className="border-b hover:bg-gray-50">
                      <td className="p-3">{formatDate(transaction.date)}</td>
                      <td className="p-3">
                        <span className={`px-2 py-1 text-xs rounded-full flex items-center gap-1 w-fit ${
                          transaction.type === 'Receive' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {transaction.type === 'Receive' ? <ArrowDownCircle className="w-3 h-3" /> : <ArrowUpCircle className="w-3 h-3" />}
                          {transaction.type}
                        </span>
                      </td>
                      <td className="p-3 font-medium">{transaction.reference}</td>
                      <td className="p-3">
                        {transaction.type === 'Receive' ? `From: ${transaction.supplier}` : `To: ${transaction.issued_to}`}
                      </td>
                      <td className="p-3 font-medium">
                        <span className={transaction.type === 'Receive' ? 'text-green-600' : 'text-red-600'}>
                          {transaction.type === 'Receive' ? '+' : '-'}₹{transaction.amount}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default InventoryLedger;